/**
 * Auth mutation hooks — login, signup, logout.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import UsersAPI from '@/api/users.api';
import { useAuthStore } from '@/stores/auth.store';
import type { LoginRequest, SignupRequest } from '@/api/types';

export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: LoginRequest) => UsersAPI.login(data),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(['currentUser'], user);
      // Redirect to admin if role is admin OR email is in the admin whitelist
      const isAdmin = user.userRole === 'admin' || user.email === 'lirmakym@mail.ru';
      navigate(isAdmin ? '/admin' : '/');
    },
  });
}

export function useSignup() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setUser } = useAuthStore();

  return useMutation({
    mutationFn: (data: SignupRequest) => UsersAPI.signup(data),
    onSuccess: (user) => {
      setUser(user);
      queryClient.setQueryData(['currentUser'], user);
      navigate('/');
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { clearUser } = useAuthStore();

  return useMutation({
    mutationFn: async () => UsersAPI.logout(),
    onSuccess: () => {
      clearUser();
      queryClient.clear();
      navigate('/login');
    },
  });
}

export function useRecoverPassword() {
  return useMutation({
    mutationFn: (email: string) => UsersAPI.recoverPassword(email),
  });
}
